/**
 * Coque du back-office (`/admin/*`) : barre latérale groupée, en-tête avec
 * l’utilisateur connecté et déconnexion.
 *
 * Sobre par choix : c’est l’outil de travail quotidien d’Anne et de Luc, pas
 * une vitrine. Densité d’information élevée, aucune animation gratuite.
 */

import { Suspense, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'

import { LIBELLES_ROLE_UTILISATEUR } from '@tb/shared'

import { useAuth, useUtilisateur } from '../auth'
import { cn } from '../lib/cn'
import { CHEMINS } from '../lib/chemins'
import { initiales } from '../lib/format'
import { useTheatre } from '../lib/theatre'
import { Button } from '../ui/Button'
import { ChargementPage } from '../ui/Spinner'
import { BandeauEcheance, EcranTenantBloquant, useEtatTenantBloquant } from './EtatTenant'
import { LienAide } from './LienAide'
import { MonMotDePasse } from './MonMotDePasse'
import { aidePourChemin, navigationPour } from './navigation'

/** `md` de Tailwind : au-delà, la barre latérale est permanente. */
const REQUETE_BARRE_FIXE = '(min-width: 768px)'

const ID_TIROIR = 'navigation-back-office'

const SELECTEUR_FOCALISABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

function estBarreFixe(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return window.matchMedia(REQUETE_BARRE_FIXE).matches
}

function focalisables(racine: HTMLElement): HTMLElement[] {
  return [...racine.querySelectorAll<HTMLElement>(SELECTEUR_FOCALISABLE)]
}

export function LayoutAdmin() {
  const utilisateur = useUtilisateur()
  const { deconnecter } = useAuth()
  const naviguer = useNavigate()
  /* La référence de documentation de l'écran servi — page ET section quand une
     section le décrit mieux que l'introduction. Voir `aidePourChemin`. */
  const referenceAide = aidePourChemin(useLocation().pathname)
  const { nom: nomTheatre, type: typeTenant } = useTheatre()
  const etatTenant = useEtatTenantBloquant()
  const [menuOuvert, setMenuOuvert] = useState(false)
  const [motDePasseOuvert, setMotDePasseOuvert] = useState(false)
  /** Vrai dès que la barre latérale est permanente (≥ md) : plus de tiroir. */
  const [barreFixe, setBarreFixe] = useState(() => estBarreFixe())
  const boutonMenu = useRef<HTMLButtonElement>(null)
  const tiroir = useRef<HTMLElement>(null)

  /* ★ EVT-04 — l'interface allégée d'un événement ponctuel. Le type vient de
     `GET /theatre/publique`, donc du registre de plateforme : la base d'un
     théâtre ne sait pas ce qu'elle est. */
  const groupes = navigationPour(utilisateur.role, typeTenant, utilisateur.droitsMetier)

  async function seDeconnecter(): Promise<void> {
    await deconnecter()
    await naviguer(CHEMINS.connexionStaff, { replace: true })
  }

  /* Le tiroir n’existe qu’en dessous de `md` : au-delà la barre est permanente
     et ne doit ni être rendue inerte, ni verrouiller le défilement. */
  useEffect(() => {
    const requete = window.matchMedia(REQUETE_BARRE_FIXE)
    const suivre = (): void => {
      setBarreFixe(requete.matches)
      // Barre devenue permanente (rotation en paysage, iPad) : le tiroir n’a
      // plus lieu d’être, sinon son voile resterait armé en arrière-plan.
      if (requete.matches) setMenuOuvert(false)
    }
    suivre()
    requete.addEventListener('change', suivre)
    return () => {
      requete.removeEventListener('change', suivre)
    }
  }, [])

  /* Échap ferme le tiroir et rend le focus au bouton qui l’a ouvert (WCAG 2.1.2). */
  useEffect(() => {
    if (!menuOuvert) return undefined
    const auClavier = (evenement: KeyboardEvent): void => {
      if (evenement.key !== 'Escape') return
      evenement.preventDefault()
      setMenuOuvert(false)
      boutonMenu.current?.focus()
    }
    document.addEventListener('keydown', auClavier)
    return () => {
      document.removeEventListener('keydown', auClavier)
    }
  }, [menuOuvert])

  /* La page ne défile plus sous le tiroir — même verrou que `Modal`. */
  useEffect(() => {
    if (!menuOuvert || barreFixe) return undefined
    const precedent = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = precedent
    }
  }, [menuOuvert, barreFixe])

  /* Piège de focus : le premier lien reçoit le focus à l’ouverture, et la
     tabulation ne quitte plus le tiroir tant qu’il est ouvert. */
  useEffect(() => {
    if (!menuOuvert || barreFixe) return undefined
    const nav = tiroir.current
    if (nav === null) return undefined

    focalisables(nav)[0]?.focus()

    const auClavier = (evenement: KeyboardEvent): void => {
      if (evenement.key !== 'Tab') return
      const cibles = focalisables(nav)
      const premier = cibles[0]
      const dernier = cibles[cibles.length - 1]
      if (premier === undefined || dernier === undefined) return
      const actif = document.activeElement
      if (evenement.shiftKey && (actif === premier || !nav.contains(actif))) {
        evenement.preventDefault()
        dernier.focus()
      } else if (!evenement.shiftKey && (actif === dernier || !nav.contains(actif))) {
        evenement.preventDefault()
        premier.focus()
      }
    }
    document.addEventListener('keydown', auClavier)
    return () => {
      document.removeEventListener('keydown', auClavier)
    }
  }, [menuOuvert, barreFixe])

  // Tiroir fermé : ses 11 liens ne doivent être ni tabulables ni annoncés.
  // `inert` fait les deux d’un coup — mais jamais quand la barre est permanente.
  const tiroirInerte = !barreFixe && !menuOuvert

  // LOT F3 — tenant expiré/suspendu : toute requête échoue déjà (§EtatTenant),
  // la coque normale n'aurait donc rien à afficher au-delà d'erreurs en cascade.
  if (etatTenant !== undefined) return <EcranTenantBloquant etat={etatTenant} />

  return (
    <div className="min-h-dvh bg-slate-50">
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Aller au contenu
      </a>

      <BandeauEcheance />

      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4">
        <button
          ref={boutonMenu}
          type="button"
          className="-ml-1 rounded p-3 text-slate-600 hover:bg-slate-100 md:hidden"
          aria-label={menuOuvert ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={menuOuvert}
          aria-controls={ID_TIROIR}
          onClick={() => {
            setMenuOuvert((ouvert) => !ouvert)
          }}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            className="size-5"
          >
            <path d="M3 6h14M3 10h14M3 14h14" strokeLinecap="round" />
          </svg>
        </button>

        {/* `py-3` : 44 px de haut au doigt, sans décaler l’alignement sur la
            ligne de base des deux libellés. */}
        <Link to={CHEMINS.admin} className="flex min-w-0 items-baseline gap-2 py-3 sm:py-0">
          <span className="truncate font-semibold text-slate-900">{nomTheatre}</span>
          <span className="hidden text-xs tracking-wide text-slate-500 uppercase sm:inline">
            Back-office
          </span>
        </Link>

        <div className="ml-auto flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <p className="text-sm leading-tight font-medium text-slate-900">{utilisateur.nom}</p>
            <p className="text-xs leading-tight text-slate-500">
              {LIBELLES_ROLE_UTILISATEUR[utilisateur.role]}
            </p>
          </div>
          <span
            className="flex size-9 items-center justify-center rounded-full bg-rideau-100 text-sm font-semibold text-rideau-800"
            aria-hidden="true"
          >
            {initiales(utilisateur.nom)}
          </span>
          {/* ★ « Mon mot de passe » vit ICI et pas dans la configuration : c'est
              le seul endroit commun aux trois rôles, et c'est le guichet —
              qui n'a pas accès à la configuration — qui en a le plus besoin. */}
          <Button
            variante="discret"
            taille="sm"
            onClick={() => {
              setMotDePasseOuvert(true)
            }}
          >
            Mon mot de passe
          </Button>
          <Button
            variante="secondaire"
            taille="sm"
            onClick={() => {
              void seDeconnecter()
            }}
          >
            Déconnexion
          </Button>
        </div>
      </header>

      <MonMotDePasse
        ouvert={motDePasseOuvert}
        onFermer={() => {
          setMotDePasseOuvert(false)
        }}
      />

      <div className="flex">
        {/* Voile de fermeture du menu mobile. */}
        {menuOuvert && (
          <button
            type="button"
            className="fixed inset-0 z-20 bg-slate-900/30 md:hidden"
            aria-label="Fermer le menu"
            onClick={() => {
              setMenuOuvert(false)
            }}
          />
        )}

        <nav
          ref={tiroir}
          id={ID_TIROIR}
          aria-label="Navigation principale"
          // `inert` (et non `aria-hidden` seul) : sans lui, les 11 liens du
          // tiroir fermé restent dans l’ordre de tabulation, hors écran.
          inert={tiroirInerte}
          className={cn(
            'fixed inset-y-0 left-0 z-30 w-64 shrink-0 overflow-y-auto border-r border-slate-200 bg-white px-3 py-4 transition-transform md:sticky md:top-14 md:z-0 md:h-[calc(100dvh-3.5rem)] md:translate-x-0',
            menuOuvert ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <ul className="flex flex-col gap-5">
            {groupes.map((groupe) => (
              <li key={groupe.titre}>
                {/* `slate-600` et non `slate-400` : à 12 px, un titre de groupe
                    doit franchir 4,5:1 sur blanc (WCAG 1.4.3 ; slate-400 =
                    2,63:1, slate-500 = 4,6:1, slate-600 = 7,1:1). */}
                <p className="px-2 pb-1 text-xs font-semibold tracking-wider text-slate-600 uppercase">
                  {groupe.titre}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {groupe.entrees.map((entree) => (
                    <li key={entree.to}>
                      <NavLink
                        to={entree.to}
                        onClick={() => {
                          setMenuOuvert(false)
                        }}
                        // `end` sur la racine d’un espace, sinon tout resterait actif.
                        end={entree.to === CHEMINS.admin}
                        className={({ isActive }) =>
                          cn(
                            'block rounded-md px-2 py-3 text-sm transition-colors md:py-1.5',
                            isActive && entree.externe !== true
                              ? 'bg-rideau-50 font-medium text-rideau-800'
                              : 'text-slate-700 hover:bg-slate-100',
                          )
                        }
                        title={entree.description}
                      >
                        {entree.libelle}
                        {entree.externe === true && (
                          <span className="ml-1 text-slate-600" aria-hidden="true">
                            ↗
                          </span>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>

        <main id="contenu" className="min-w-0 flex-1 px-4 py-6 lg:px-8">
          {/**
           * ★ LE LIEN D'AIDE DE L'ÉCRAN COURANT — posé ICI, une seule fois,
           * plutôt que dans chacun des quinze écrans.
           *
           * La référence vient de `navigation.ts`, qui connaît déjà chaque
           * écran : ajouter un écran au menu lui donne son aide du même geste,
           * et `navigation-aide.test.ts` refuse qu'on l'oublie. Poser le lien
           * écran par écran aurait garanti l'inverse — quatorze écrans servis
           * et le quinzième muet, le motif que ce projet a passé six vagues à
           * rattraper.
           *
           * ★ Ce lien-ci vise l'écran ENTIER, et il vise désormais la SECTION
           * qui le décrit quand il y en a une. Les liens qui répondent à une
           * question précise — le tarif manquant, le verrou du plan, la
           * dérogation de phase — sont posés AU POINT D'USAGE, dans l'écran, à
           * côté de ce qui pose la question. Les deux ne se remplacent pas :
           * celui-ci se trouve quand on ne sait pas quoi chercher.
           *
           * Il est aligné à droite et discret : c'est un recours, pas une
           * invitation. Sur les rares adresses qu'aucune entrée ne couvre, il
           * ne s'affiche pas du tout — mieux vaut aucun lien qu'un lien vers
           * une rubrique qui ne parle pas de cet écran.
           */}
          {referenceAide !== undefined && (
            <div className="mb-4 flex justify-end">
              <LienAide reference={referenceAide} libelle="Aide sur cet écran" />
            </div>
          )}

          {/* Écrans chargés à la demande (ARCHITECTURE §9.4, découpage par
              feature) : ce repli couvre le temps de récupération du chunk. */}
          <Suspense fallback={<ChargementPage libelle="Chargement de l’écran…" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  )
}
